import { z } from 'zod';
import { LookSchema } from '../data/look.js';
import { AppearanceSchema } from './appearance.js';
import { PARTY_MAX_SIZE } from '../rules/party.js';
/**
 * Parties (ADR 0032). A client names a player to invite, answers an
 * invitation, leaves, or — leading — sends a member away. The server keeps
 * the parties, tells each member the whole party after every change, and
 * never takes a member list from a client.
 */
const CharacterIdSchema = z.string().min(1).max(64);
/** Invite a player by name, wherever they are in the game. */
export const PartyInviteMessageSchema = z.object({
    name: z.string().trim().min(1).max(32),
});
/** Answer the invitation of `from` (a character id, as `party_invited` gave it). */
export const PartyAnswerMessageSchema = z.object({
    from: CharacterIdSchema,
    accept: z.boolean(),
});
export const PartyLeaveMessageSchema = z.object({}).strict();
/** The leader sends a member away. */
export const PartyKickMessageSchema = z.object({ characterId: CharacterIdSchema });
/**
 * What a portrait is drawn from, exactly what the zone already shows of a
 * player: the class's body, hair and eyes, and the template ids of the
 * visible pieces. Never the inventory.
 */
export const CharacterPortraitSchema = z.object({
    classId: z.string().min(1),
    look: LookSchema,
    appearance: AppearanceSchema,
});
/**
 * One member as the others see them in the party frames: their portrait,
 * a health bar and where they are. No stats, no inventory.
 */
export const PartyMemberSchema = CharacterPortraitSchema.extend({
    id: CharacterIdSchema,
    name: z.string().min(1),
    level: z.number().int().min(1),
    hp: z.number().int().min(0),
    maxHp: z.number().int().min(1),
    zoneId: z.string().min(1),
    /** Out of the game, their seat kept for `PARTY_OFFLINE_GRACE_MS`. */
    online: z.boolean(),
    /** In the game but away from the keyboard (ADR 0080). */
    away: z.boolean().default(false),
});
/**
 * To every member after each change, and to someone who leaves: the whole
 * party in joining order, the leader first. Empty members: no party.
 */
export const PartyStateSchema = z.object({
    leaderId: CharacterIdSchema.nullable(),
    members: z.array(PartyMemberSchema).max(PARTY_MAX_SIZE),
});
/** To the invited player: who asks, and how long the question stands. */
export const PartyInvitedSchema = z.object({
    from: CharacterIdSchema,
    name: z.string().min(1),
    expiresInMs: z.number().int().min(0),
});
/** What the party hears happen; the frames follow from `party` itself. */
export const PARTY_EVENT_KINDS = [
    /** Sent to the one who invited: the invitation is on its way. */
    'invited',
    'joined',
    'left',
    'kicked',
    'declined',
    'expired',
    'leader',
    'disbanded',
];
export const PartyEventSchema = z.object({
    kind: z.enum(PARTY_EVENT_KINDS),
    /** The player it happened to. */
    name: z.string().min(1),
});
export const PARTY_REFUSALS = [
    'invalid',
    /** Nobody of that name is in the game. */
    'not-found',
    'self',
    /** The invited player is already in a party. */
    'already-grouped',
    'full',
    /** Only the leader invites and sends away. */
    'not-leader',
    /** The invitation answered no longer stands. */
    'no-invite',
    /** An invitation to that player is already waiting. */
    'pending',
    'not-in-party',
];
export const PartyRejectedSchema = z.object({
    reason: z.enum(PARTY_REFUSALS),
});
//# sourceMappingURL=party.js.map